import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { t } from '../lib/i18n';
import { MembersDrawer } from './MembersDrawer';
import { messageFor } from '../lib/errors';
import { useAuth } from '../auth/auth-store';
import { useRealtime, useRealtimeReconnect, useRealtimeTopic } from '../app/realtime-store';
import { formatDate, formatTime } from '../ui/format';
import { EmptyState, ErrorState, Loading, PageHeader } from '../ui/Page';
import { Avatar } from '../ui/Avatar';
import { PinIcon } from '../app/nav/icons';
import { useMembers } from '../ui/UserSelect';
import { dayLabel, groupMessages } from './grouping';
import {
  createChannel,
  joinConversation,
  listChatConversations,
  listChatMessages,
  markRead,
  openDirect,
  pinConversation,
  sendChatMessage,
  type ChatConversation,
  type ChatMessage,
} from './api';

/**
 * Internal chat (context.md sections 57-59).
 *
 * Colleagues, not customers — a conversation with somebody outside the tenant is the inbox, and the
 * two are deliberately different screens because they are different work: no SLA here, no delivery
 * status, everyone has an account.
 *
 * **It has its own markup and its own stylesheet, and that is the point of Phase 124.** It used to
 * borrow the inbox's classes; the inbox then grew a side panel and its `.thread` became a row, so
 * this screen's header, messages and composer silently became three columns side by side, with the
 * header at the bottom of its own. Borrowed styling breaks when the neighbour moves, and nothing
 * tells you.
 *
 * The other half of the rewrite is a decision about how a team room reads. Messages are **rows, not
 * bubbles**: a chat with a named colleague on every line is read top to bottom like a transcript,
 * and consecutive lines from one person inside a few minutes are one thing they said, under one
 * name and one time. Bubbles alternating left and right are for a two-person conversation on a
 * phone, and in a channel with six people they turn a discussion into a tennis match.
 *
 * Live over the tab's one socket rather than polled. A room is a topic on it.
 */
export function ChatPage(): React.JSX.Element {
  const client = useQueryClient();
  const { user } = useAuth();
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const conversations = useQuery({
    queryKey: ['chat-conversations'],
    queryFn: listChatConversations,
  });

  /*
   * Memoised because an effect depends on it.
   *
   * `data?.x ?? []` is a new array on every render, so an effect listing it as a dependency runs on
   * every render — here that is a `setSelected` call per render, which is a render loop waiting for
   * a condition to hold. Cheap to get right, expensive to find later.
   */
  const rows = useMemo(
    () => conversations.data?.conversations ?? [],
    [conversations.data?.conversations],
  );

  const current = rows.find((row) => row.id === selected) ?? null;

  /*
   * Being invited to a channel, without a reload.
   *
   * The topic is the person, not the room: at the moment somebody adds you there is no room in this
   * list to have been listening to.
   */
  useRealtimeTopic(user ? `chat_user:${user.id}` : null, () => {
    void client.invalidateQueries({ queryKey: ['chat-conversations'] });
  });

  useEffect(() => {
    if (rows.length === 0) {
      setSelected(null);
      return;
    }

    if (!selected || !rows.some((row) => row.id === selected)) setSelected(rows[0]!.id);
  }, [rows, selected]);

  /*
   * Opening on the room the message bell pointed at.
   *
   * Read once and cleared, so the choice above stays free to move afterwards — otherwise every
   * refetch would drag the person back to this room.
   */
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    const wanted = params.get('sala');

    if (!wanted) return;

    setSelected(wanted);
    setParams({}, { replace: true });
  }, [params, setParams]);

  /*
   * Pinned rooms come out of their sections and sit above both.
   *
   * The point of pinning is that the room is always in the same place; leaving it inside "Canais"
   * or "Diretas" means it still moves whenever the sections do, which is the thing being fixed.
   * The server already returns them first, so the order among several is the order they were
   * pinned in.
   */
  /*
   * What the filter leaves, before the sections split it.
   *
   * Matched on the name a person reads rather than on the row, so a direct conversation — whose
   * `name` is null and which is drawn from the other member — is still findable by that person's
   * name. Filtering the raw field would make exactly those rows unsearchable.
   */
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return needle ? rows.filter((row) => nameOf(row).toLowerCase().includes(needle)) : rows;
  }, [rows, query]);

  const pinned = shown.filter((row) => row.pinned);
  const channels = shown.filter((row) => !row.pinned && row.type === 'CHANNEL');
  const directs = shown.filter((row) => !row.pinned && row.type !== 'CHANNEL');

  const pin = useMutation({
    mutationFn: (input: { id: string; pinned: boolean }) =>
      pinConversation(input.id, input.pinned),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['chat-conversations'] });
      // The bell's own list is sorted the same way, so it has to hear about this too.
      void client.invalidateQueries({ queryKey: ['chat-unread'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <div className="view view--flush">
      <PageHeader title={t('nav.chat')} description={t('chat.description')} />

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <div className={`chat${selected ? ' chat--reading' : ''}`}>
        <aside className="chat__rooms" aria-label={t('chat.conversations')}>
          <NewChannel
            onCreated={(id) => {
              setSelected(id);
              setError(null);
            }}
            onError={setError}
          />

          {/*
            Messaging a colleague, which this screen could not do.

            `POST /v1/chat/direct` has existed since the module was built and nothing called it, so
            the "Diretas" section below could only ever be empty — a team chat with no way to reach
            a person in it. Choosing somebody opens the conversation, creating it the first time.
          */}
          <DirectPicker
            onOpened={(id) => {
              setSelected(id);
              setError(null);
            }}
            onError={setError}
          />

          {/* Only once the list is long enough that reading it is slower than typing. */}
          {rows.length > 8 && (
            <div className="chat__filter">
              <input
                type="search"
                className="input"
                value={query}
                placeholder={t('chat.filterPlaceholder')}
                aria-label={t('chat.filterPlaceholder')}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
          )}

          {conversations.error ? (
            <ErrorState error={conversations.error} onRetry={() => void conversations.refetch()} />
          ) : conversations.isLoading ? (
            <Loading />
          ) : rows.length === 0 ? (
            <EmptyState title={t('chat.empty')} description={t('chat.emptyBody')} />
          ) : (
            <div className="chat__sections">
              {pinned.length > 0 && (
                <RoomList
                  label={t('chat.pinned')}
                  rooms={pinned}
                  selected={selected}
                  onSelect={setSelected}
                  onPin={(id, next) => pin.mutate({ id, pinned: next })}
                />
              )}

              {channels.length > 0 && (
                <RoomList
                  label={t('chat.channels')}
                  rooms={channels}
                  selected={selected}
                  onSelect={setSelected}
                  onPin={(id, next) => pin.mutate({ id, pinned: next })}
                />
              )}

              {directs.length > 0 && (
                <RoomList
                  label={t('chat.directs')}
                  rooms={directs}
                  selected={selected}
                  onSelect={setSelected}
                  onPin={(id, next) => pin.mutate({ id, pinned: next })}
                />
              )}
            </div>
          )}
        </aside>

        <section className="chat__room" aria-label={t('chat.thread')}>
          {/*
            `selected` can name a conversation the list does not have yet — creating a channel
            selects it before the listing refetches. Resolving it here rather than asserting it
            means that gap shows a spinner instead of throwing.
          */}
          {current ? (
            <Room key={current.id} conversation={current} onBack={() => setSelected(null)} />
          ) : selected ? (
            <Loading />
          ) : (
            <EmptyState title={t('chat.pick')} description={t('chat.pickBody')} />
          )}
        </section>
      </div>
    </div>
  );
}

function RoomList({
  label,
  rooms,
  selected,
  onSelect,
  onPin,
}: {
  label: string;
  rooms: ChatConversation[];
  selected: string | null;
  onSelect: (id: string) => void;
  onPin: (id: string, pinned: boolean) => void;
}): React.JSX.Element {
  return (
    <div className="chat__section">
      <p className="chat__section-label">{label}</p>

      <ul className="chat__list">
        {rooms.map((room) => (
          <li key={room.id}>
            <button
              type="button"
              className={`chat-room${room.id === selected ? ' chat-room--active' : ''}${
                room.unread > 0 ? ' chat-room--unread' : ''
              }`}
              onClick={() => onSelect(room.id)}
            >
              {/* The hash is the convention for a channel, and it does the work a label would. */}
              <span className="chat-room__mark" aria-hidden="true">
                {room.type === 'CHANNEL' ? '#' : '@'}
              </span>

              <span className="chat-room__name">{nameOf(room)}</span>

              {room.unread > 0 && <span className="chat-room__unread">{room.unread}</span>}
            </button>

            {/*
              Outside the room button, not inside it.
              
              A button within a button is invalid markup and the browser resolves it by dropping
              one of them — which is how a pin ends up opening the room instead. Only offered for
              rooms somebody actually belongs to: the server refuses the rest, and a control that
              always fails is worse than no control.
            */}
            {room.member && (
              <button
                type="button"
                className={`chat-room__pin${room.pinned ? ' is-pinned' : ''}`}
                aria-pressed={room.pinned}
                title={room.pinned ? t('chat.unpin') : t('chat.pin')}
                aria-label={room.pinned ? t('chat.unpin') : t('chat.pin')}
                onClick={() => onPin(room.id, !room.pinned)}
              >
                <PinIcon />
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Creating a channel, behind a button.
 *
 * It used to be a text box permanently at the top of the list, which is a form somebody sees a
 * hundred times for a thing they do twice a year — and which pushed the rooms down the screen.
 */
function NewChannel({
  onCreated,
  onError,
}: {
  onCreated: (id: string) => void;
  onError: (message: string | null) => void;
}): React.JSX.Element {
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');

  const create = useMutation({
    mutationFn: () => createChannel({ name: name.trim() }),
    onSuccess: (channel) => {
      setName('');
      setOpen(false);
      onCreated(channel.id);
      void client.invalidateQueries({ queryKey: ['chat-conversations'] });
    },
    onError: (caught: unknown) => onError(messageFor(caught)),
  });

  return (
    <div className="chat__rooms-head">
      <span className="chat__rooms-title">{t('chat.rooms')}</span>

      <button
        type="button"
        className="chat__new"
        aria-label={t('chat.newChannel')}
        title={t('chat.newChannel')}
        onClick={() => setOpen((current) => !current)}
      >
        +
      </button>

      {open && (
        <form
          className="chat__new-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          <input
            className="input"
            value={name}
            autoFocus
            maxLength={80}
            placeholder={t('chat.newChannelPlaceholder')}
            onChange={(event) => setName(event.target.value)}
          />

          <button
            type="submit"
            className="button button--primary button--compact"
            disabled={name.trim() === '' || create.isPending}
          >
            {t('chat.create')}
          </button>
        </form>
      )}
    </div>
  );
}

/**
 * Starting a conversation with a colleague.
 *
 * The one thing a team chat has to do that this screen could not. Opening is idempotent on the
 * server — a `direct_key` makes "the conversation between these two people" a single row — so
 * choosing somebody twice lands in the same place rather than creating a second thread.
 *
 * Yourself is left out of the list: a conversation with one member has nobody to talk to, and the
 * server would refuse it anyway.
 */
function DirectPicker({
  onOpened,
  onError,
}: {
  onOpened: (id: string) => void;
  onError: (message: string | null) => void;
}): React.JSX.Element | null {
  const client = useQueryClient();
  const { user } = useAuth();
  const { options: people } = useMembers();

  const open = useMutation({
    mutationFn: (userId: string) => openDirect(userId),
    onSuccess: (conversation) => {
      onOpened(conversation.id);
      void client.invalidateQueries({ queryKey: ['chat-conversations'] });
    },
    onError: (caught: unknown) => onError(messageFor(caught)),
  });

  const others = people.filter((person) => person.id !== user?.id);

  // A tenant of one has nobody to message; the control would only ever be empty.
  if (others.length === 0) return null;

  return (
    <div className="chat__filter">
      <select
        className="select select--compact"
        value=""
        disabled={open.isPending}
        aria-label={t('chat.newDirect')}
        onChange={(event) => {
          if (event.target.value) open.mutate(event.target.value);
        }}
      >
        <option value="">{t('chat.newDirect')}</option>
        {others.map((person) => (
          <option key={person.id} value={person.id}>
            {person.name}
          </option>
        ))}
      </select>
    </div>
  );
}

function Room({
  conversation,
  onBack,
}: {
  conversation: ChatConversation;
  onBack: () => void;
}): React.JSX.Element {
  const { user } = useAuth();
  const { options: people } = useMembers();
  const client = useQueryClient();
  const bottom = useRef<HTMLDivElement>(null);

  const [membersOpen, setMembersOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { connected: live } = useRealtime();

  const history = useQuery({
    queryKey: ['chat-messages', conversation.id],
    queryFn: () => listChatMessages(conversation.id),
  });

  /**
   * The room, as a topic.
   *
   * What arrives is pushed into the same query cache the history came from, so there is one list
   * rather than two to reconcile — and an edit or a deletion is applied in place rather than
   * leaving a message on screen that no longer exists.
   */
  useRealtimeTopic(`chat:${conversation.id}`, (event) => {
    client.setQueryData<{ messages: ChatMessage[]; next_cursor: string | null }>(
      ['chat-messages', conversation.id],
      (currentData) => {
        if (!currentData) {
          // Nothing loaded yet: the fetch in flight will carry this message.
          return currentData;
        }

        if (event.type === 'message.created') {
          const incoming = event.payload as ChatMessage;

          // The sender already has its own message from the POST; the echo must not double it.
          if (currentData.messages.some((message) => message.id === incoming.id)) return currentData;

          return { ...currentData, messages: [...currentData.messages, incoming] };
        }

        if (event.type === 'message.edited') {
          const edited = event.payload as { id: string; body: string; edited_at?: string };

          return {
            ...currentData,
            messages: currentData.messages.map((message) =>
              message.id === edited.id
                ? { ...message, body: edited.body, edited_at: edited.edited_at ?? null }
                : message,
            ),
          };
        }

        if (event.type === 'message.deleted') {
          const { id } = event.payload as { id: string };

          return {
            ...currentData,
            messages: currentData.messages.filter((message) => message.id !== id),
          };
        }

        return currentData;
      },
    );
  });

  /*
   * A socket that was down missed whatever happened while it was down.
   *
   * The history is read again on reconnect rather than trusted to have stayed complete: this is the
   * part that makes the difference between "live" and "live until the wifi blinks".
   */
  useRealtimeReconnect(() => {
    void client.invalidateQueries({ queryKey: ['chat-messages', conversation.id] });
    void client.invalidateQueries({ queryKey: ['chat-conversations'] });
  });

  const messages = useMemo(() => history.data?.messages ?? [], [history.data?.messages]);
  const sections = useMemo(() => groupMessages(messages), [messages]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
    void markRead(conversation.id).catch(() => undefined);
  }, [messages.length, conversation.id]);

  const send = useMutation({
    mutationFn: () => sendChatMessage(conversation.id, draft.trim()),
    onSuccess: (message) => {
      setDraft('');
      setError(null);

      client.setQueryData<{ messages: ChatMessage[]; next_cursor: string | null }>(
        ['chat-messages', conversation.id],
        (currentData) =>
          currentData && !currentData.messages.some((entry) => entry.id === message.id)
            ? { ...currentData, messages: [...currentData.messages, message] }
            : currentData,
      );
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const join = useMutation({
    mutationFn: () => joinConversation(conversation.id),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['chat-conversations'] }),
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <div className="chat-thread">
      <header className="chat-thread__header">
        <button
          type="button"
          className="chat-thread__back"
          onClick={onBack}
          aria-label={t('inbox.back')}
        >
          ←
        </button>

        <div className="chat-thread__who">
          <p className="chat-thread__title">
            <span aria-hidden="true">{conversation.type === 'CHANNEL' ? '#' : '@'}</span>
            {nameOf(conversation)}
          </p>
          <p className="muted small">
            {conversation.topic ?? t(`chat.type.${conversation.type}`)}
          </p>
        </div>

        <div className="chat-thread__actions">
          {conversation.type === 'CHANNEL' && (
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => setMembersOpen((currentOpen) => !currentOpen)}
            >
              {t('chat.members')}
            </button>
          )}

          {/*
            * Presence of the socket, not of a person.
            *
            * It says whether this screen is receiving; a room that has gone quiet because the
            * connection dropped looks exactly like a room where nobody is talking, and that is the
            * one thing a live chat must never leave ambiguous.
            */}
          <span
            className={`live${live ? ' live--on' : ''}`}
            title={live ? t('chat.live') : t('chat.offline')}
          >
            <span className="live__dot" aria-hidden="true" />
            {live ? t('chat.live') : t('chat.offline')}
          </span>
        </div>
      </header>

      {membersOpen && (
        <MembersDrawer conversationId={conversation.id} onClose={() => setMembersOpen(false)} />
      )}

      <div className="chat-thread__messages">
        {history.isLoading ? (
          <Loading />
        ) : sections.length === 0 ? (
          <EmptyState title={t('chat.noMessages')} description={t('chat.noMessagesBody')} />
        ) : (
          sections.map((section) => (
            <div key={section.day} className="chat-day">
              <p className="chat-day__divider">
                <span>{dayName(section.day)}</span>
              </p>

              {section.groups.map((group) => {
                const author = people.find((person) => person.id === group.authorId);
                const mine = group.authorId === user?.id;

                return (
                  <article
                    key={group.messages[0]!.id}
                    className={`chat-group${mine ? ' chat-group--mine' : ''}`}
                  >
                    <Avatar
                      size="sm"
                      name={author?.name ?? t('chat.someone')}
                      url={author?.avatarUrl}
                    />

                    <div className="chat-group__body">
                      <p className="chat-group__head">
                        <span className="chat-group__author">
                          {author?.name ?? t('chat.someone')}
                        </span>
                        <span className="chat-group__time">{formatTime(group.at)}</span>
                      </p>

                      {group.messages.map((message) => (
                        <p key={message.id} className="chat-line">
                          {message.body}
                          {message.edited_at && (
                            <span className="chat-line__edited">{t('chat.edited')}</span>
                          )}
                        </p>
                      ))}
                    </div>
                  </article>
                );
              })}
            </div>
          ))
        )}

        <div ref={bottom} />
      </div>

      <footer className="chat-composer">
        {error && (
          <p className="alert alert--compact" role="alert">
            {error}
          </p>
        )}

        {!conversation.member ? (
          <div className="chat-composer__join">
            <span className="muted small">{t('chat.notMember')}</span>
            <button
              type="button"
              className="button button--primary button--compact"
              disabled={join.isPending}
              onClick={() => join.mutate()}
            >
              {t('chat.join')}
            </button>
          </div>
        ) : (
          <div className="chat-composer__box">
            <textarea
              className="chat-composer__input"
              value={draft}
              rows={1}
              placeholder={t('chat.placeholder').replace('{room}', nameOf(conversation))}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey && draft.trim()) {
                  event.preventDefault();
                  send.mutate();
                }
              }}
            />

            <div className="chat-composer__foot">
              <span className="muted small">{t('chat.hint')}</span>

              <button
                type="button"
                className="button button--primary button--compact"
                disabled={draft.trim() === '' || send.isPending}
                onClick={() => send.mutate()}
              >
                {t('inbox.send')}
              </button>
            </div>
          </div>
        )}
      </footer>
    </div>
  );
}

/** A direct conversation has no name of its own; it is named by who is in it. */
/**
 * What to call a conversation.
 *
 * A channel has a name. A direct conversation does not, and used to fall back to the words "conversa
 * direta" — which is fine while a person has one and useless the moment they have two. The server
 * now says who is on the other side, and that is the name.
 */
function nameOf(conversation: ChatConversation): string {
  return conversation.name ?? conversation.partner_name ?? t('chat.direct');
}

/** "Hoje", "Ontem", or the date — decided by the pure rule in `grouping.ts`. */
function dayName(day: string): string {
  const which = dayLabel(day);

  if (which === 'TODAY') return t('chat.today');
  if (which === 'YESTERDAY') return t('chat.yesterday');

  return formatDate(day);
}
